import React, { useState, useEffect } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import api from '../utils/api';
import './StaffDashboard.css';

function StaffDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    return window.innerWidth > 768;
  });
  const [isMobile, setIsMobile] = React.useState(() => {
    return window.innerWidth <= 768;
  });
  const [showProfileModal, setShowProfileModal] = React.useState(false);
  const [passwordForm, setPasswordForm] = React.useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordError, setPasswordError] = React.useState('');
  const [passwordSuccess, setPasswordSuccess] = React.useState('');
  const [changingPassword, setChangingPassword] = React.useState(false);
  const [unreadAlertsCount, setUnreadAlertsCount] = React.useState(0);

  // Fetch unread alerts count
  const fetchUnreadCount = React.useCallback(async () => {
    try {
      const response = await api.get('/api/alerts/stats');
      if (response.data) {
        setUnreadAlertsCount(response.data.unread || 0);
      }
    } catch (error) {
      console.error('Error fetching unread alerts count:', error);
    }
  }, []);

  React.useEffect(() => {
    fetchUnreadCount();
    // Refresh every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    
    // Listen for custom event when alerts are updated
    const handleAlertUpdate = () => {
      fetchUnreadCount();
    };
    window.addEventListener('alertsUpdated', handleAlertUpdate);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('alertsUpdated', handleAlertUpdate);
    };
  }, [fetchUnreadCount]);

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }
    
    setChangingPassword(true);
    try {
      await api.post('/api/profile/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      
      setPasswordSuccess('Password changed successfully!');
      setPasswordForm({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      
      setTimeout(() => {
        setShowProfileModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Failed to change password. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;
  const isStaff = user?.role === 'staff' || isAdmin;
  const isViewer = user?.role === 'viewer' || isStaff;

  const navItems = [];
  
  // Dashboard - available to all authenticated users
  if (isViewer) {
    navItems.push({ path: '/staff/dashboard', label: 'Dashboard' });
  }
  
  // Dog Management - viewers and above (read-only for viewers)
  if (isViewer) {
    navItems.push({ path: '/staff/dogs', label: 'Dog Management' });
  }
  
  // Settings - viewers and above (read-only for viewers)
  if (isViewer) {
    navItems.push({ path: '/staff/risk-matrix', label: 'Settings' });
  }
  
  // Alerts - viewers and above
  if (isViewer) {
    navItems.push({ 
      path: '/staff/alerts', 
      label: 'Alerts & Reports',
      badge: unreadAlertsCount > 0 ? unreadAlertsCount : null
    });
  }
  
  // Live Stream - staff and above
  if (isStaff) {
    navItems.push({ path: '/staff/stream', label: 'Live Stream' });
  }

  // Found Reports - staff and above
  if (isStaff) {
    navItems.push({ path: '/staff/found-reports', label: 'Found Reports' });
  }

  // Reports - staff and above
  if (isStaff) {
    navItems.push({ path: '/staff/reports', label: 'Reports' });
  }

  // Wall Reports - staff and above
  if (isStaff) {
    navItems.push({ path: '/staff/wall-reports', label: 'Wall Reports' });
  }

  // User Management - superadmin and admin only
  if (isAdmin) {
    navItems.push({ path: '/staff/users', label: 'User Management' });
  }
  
  // Task Management - superadmin and admin only
  if (isAdmin) {
    navItems.push({ path: '/staff/tasks', label: 'Task Management' });
  }

  if (!user) {
    return null;
  }

  return (
    <div className="staff-dashboard">
      {sidebarOpen && isMobile && (
        <div 
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-top">
            <h2>Staff Portal</h2>
            <button 
              className="sidebar-close"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              ×
            </button>
          </div>
          <div className="user-info">
            <div style={{ flex: 1 }}>
              <div className="user-name">{user.username}</div>
              <div className="user-role">{user.role}</div>
            </div>
            <button
              className="profile-button"
              onClick={() => setShowProfileModal(true)}
              title="Profile Settings"
              aria-label="Profile Settings"
            >
              ⚙️
            </button>
          </div>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => {
                  if (isMobile) {
                    setSidebarOpen(false);
                  }
                  // Refresh unread count when navigating to alerts
                  if (item.path === '/staff/alerts') {
                    setTimeout(async () => {
                      try {
                        const response = await api.get('/api/alerts/stats');
                        if (response.data) {
                          setUnreadAlertsCount(response.data.unread || 0);
                        }
                      } catch (error) {
                        console.error('Error fetching unread alerts count:', error);
                      }
                    }, 500);
                  }
                }}
              >
                <span className="nav-label">{item.label}</span>
                {item.badge && item.badge > 0 && (
                  <span className="nav-badge">{item.badge > 99 ? '99+' : item.badge}</span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <button 
            className="menu-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Toggle menu"
          >
            <span className="menu-icon">☰</span>
          </button>
          <h1>Greyhound Management System</h1>
        </header>

        <div className="dashboard-content">
          <Outlet />
        </div>
      </main>

      {showProfileModal && (
        <div className="modal-overlay" onClick={() => {
          setShowProfileModal(false);
          setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
          setPasswordError('');
          setPasswordSuccess('');
        }}>
          <div className="modal-content profile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Change Password</h3>
              <button
                className="close-button"
                onClick={() => {
                  setShowProfileModal(false);
                  setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                  setPasswordError('');
                  setPasswordSuccess('');
                }}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleChangePassword} className="password-form">
              {passwordError && (
                <div className="error-message" style={{ 
                  padding: '8px', 
                  backgroundColor: '#fee2e2', 
                  color: '#dc2626', 
                  borderRadius: '4px', 
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  {passwordError}
                </div>
              )}
              
              {passwordSuccess && (
                <div className="success-message" style={{ 
                  padding: '8px', 
                  backgroundColor: '#dcfce7', 
                  color: '#16a34a', 
                  borderRadius: '4px', 
                  marginBottom: '16px',
                  fontSize: '14px'
                }}>
                  {passwordSuccess}
                </div>
              )}
              
              <div className="form-group">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  required
                  autoComplete="current-password"
                />
              </div>
              
              <div className="form-group">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
                <small style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                  Must be at least 6 characters long
                </small>
              </div>
              
              <div className="form-group">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
              </div>
              
              <div className="form-actions" style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowProfileModal(false);
                    setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                    setPasswordError('');
                    setPasswordSuccess('');
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: changingPassword ? 'not-allowed' : 'pointer',
                    opacity: changingPassword ? 0.6 : 1
                  }}
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffDashboard;

